import { CommentReactionId, UserId } from "@hex/common";
import React, { useCallback, useMemo } from "react";
import styled from "styled-components";

import { HexTooltip } from "../../hex-components";
import type { CommentModelFragment } from "../../hex-version-multiplayer/handlers/commentClientHandlers.generated";

const ReactionSpan = styled.span<{ isChecked: boolean }>`
  display: flex;
  align-items: center;
  padding: 3px 5px;

  color: ${({ theme }) => theme.fontColor.MUTED};

  font-size: ${({ theme }) => theme.fontSize.SMALL};

  border: 1px solid ${({ theme }) => theme.borderColor.MUTED};
  border-radius: ${({ theme }) => theme.borderRadius};
  cursor: pointer;

  transition: border ${({ theme }) => theme.animation.duration}
    ${({ theme }) => theme.animation.easing};

  &:hover {
    color: ${({ theme }) => theme.fontColor.DEFAULT};

    border: 1px solid ${({ theme }) => theme.borderColor.DEFAULT};
  }
`;

const ContentSpan = styled.span`
  margin-right: 8px;
`;

type Reaction = CommentModelFragment["reactions"][0];

export type CommentReactionClickHandlerArgs =
  | {
      type: "create";
      content: string;
    }
  | {
      type: "delete";
      content: string;
      reactionId: CommentReactionId;
    };

export interface CommentReactionProps {
  readonly className?: string;
  readonly reactions: readonly Reaction[];
  readonly currentUserId: UserId;
  readonly onClick: (args: CommentReactionClickHandlerArgs) => void;
}

export const CommentReaction: React.ComponentType<CommentReactionProps> =
  React.memo(function CommentReaction({
    className,
    currentUserId,
    onClick,
    reactions,
  }: CommentReactionProps) {
    const currentUserReaction = useMemo(() => {
      return reactions.find((r) => r.creatorId === currentUserId);
    }, [reactions, currentUserId]);
    const currentUserReacted = currentUserReaction != null;
    const reaction = reactions[0];

    const handleClick = useCallback(() => {
      onClick(
        currentUserReacted
          ? {
              type: "delete",
              content: reaction.content,
              reactionId: currentUserReaction?.id,
            }
          : {
              type: "create",
              content: reaction.content,
            },
      );
    }, [
      currentUserReacted,
      currentUserReaction?.id,
      onClick,
      reaction.content,
    ]);

    if (reaction == null) {
      return null;
    }

    return (
      <HexTooltip
        content={getCommentReactionTooltipText(reactions)}
        placement="top"
      >
        <ReactionSpan
          className={className}
          isChecked={currentUserReacted}
          onClick={handleClick}
        >
          <ContentSpan>{reaction.content}</ContentSpan>
          {reactions.length}
        </ReactionSpan>
      </HexTooltip>
    );
  });

const MAX_USER_COUNT = 5;

// exported for testing
export function getCommentReactionTooltipText(
  reactions: readonly Reaction[],
): string {
  let text = "";
  const count = reactions.length;
  const userNames = reactions
    .slice(0, MAX_USER_COUNT)
    .map((r) => r.creatorNameOrEmail);

  if (count === 1) {
    text = userNames[0];
  } else if (count === 2) {
    text = userNames.join(" and ");
  } else if (count === userNames.length) {
    const lastUserName = userNames.pop();
    text = `${userNames.join(", ")}, and ${lastUserName}`;
  } else {
    text = `${userNames.join(", ")}, and others`;
  }
  return text + ` reacted with ${reactions[0].content}`;
}
